import { useEffect, useRef, useState } from 'react';
import { api, formatApiError, mediaUrl } from '@/lib/api';
import { toast } from 'sonner';
import { useOnline } from '@/lib/useOnline';
import { EmptyState, Spinner } from '@/components/Common';
import {
  CameraIcon,
  UploadSimpleIcon,
  ImageIcon,
  XIcon,
  PaperclipIcon,
  CheckCircleIcon,
  TrashIcon,
} from '@phosphor-icons/react';

/**
 * MediaTab — Floor Walk Media hub.
 *
 * Captures the "drop unlimited photos in one place, tag them to findings or
 * strengths later" workflow. Works around the iOS Safari 5-photo input limit
 * by re-running the picker as many times as Vince needs and accumulating
 * uploads against the engagement.
 *
 * Capabilities:
 *  - Upload via file picker (multi-select) + dedicated camera capture button
 *  - Grid of every photo on the engagement with attach-status badge
 *  - Inline caption editing
 *  - Attach/detach to specific findings or strengths
 *  - Soft-delete
 */
export default function MediaTab({ eng }) {
  const online = useOnline();
  const [media, setMedia] = useState(null);
  const [findings, setFindings] = useState([]);
  const [strengths, setStrengths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const [mr, fr, sr] = await Promise.all([
        api.get(`/engagements/${eng.id}/media`),
        api.get(`/engagements/${eng.id}/findings`).catch(() => ({ data: [] })),
        api.get(`/engagements/${eng.id}/strengths`).catch(() => ({ data: [] })),
      ]);
      setMedia(mr.data);
      setFindings(fr.data);
      setStrengths(sr.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, [eng.id]);

  async function handleFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!online) {
      toast.error('You appear to be offline. Photos will sync once you reconnect.');
      return;
    }
    setUploadingCount(files.length);
    let uploaded = 0;
    for (const file of files) {
      try {
        const form = new FormData();
        form.append('file', file);
        form.append('caption', '');
        await api.post(`/engagements/${eng.id}/media`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded += 1;
        setUploadingCount(files.length - uploaded);
      } catch (e) {
        toast.error(`Upload failed: ${file.name} - ${formatApiError(e)}`);
      }
    }
    if (uploaded) {
      toast.success(`${uploaded} photo${uploaded > 1 ? 's' : ''} uploaded`);
    }
    setUploadingCount(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    load();
  }

  async function deleteMedia(m) {
    if (!window.confirm('Delete this photo? It will also be removed from any finding or strength it is attached to.')) return;
    try {
      await api.delete(`/media/${m.id}`);
      toast.success('Photo deleted');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (loading) return <Spinner label="Loading media..." />;

  const list = media || [];
  const unattached = list.filter((m) => !(m.attachments || []).length);
  const attached = list.filter((m) => (m.attachments || []).length);

  return (
    <div className="space-y-6">
      {/* Upload zone */}
      <UploadZone
        online={online}
        uploadingCount={uploadingCount}
        onPick={() => fileInputRef.current?.click()}
        onCamera={() => cameraInputRef.current?.click()}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="media-upload-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="media-camera-input"
      />

      {/* Stats */}
      <div className="flex flex-wrap gap-2 text-xs uppercase tracking-widest font-bold">
        <Pill label={`${list.length} TOTAL`} bg="var(--gl-dark)" fg="#fff" />
        <Pill label={`${attached.length} ATTACHED`} bg="var(--gl-p3)" fg="#fff" />
        <Pill label={`${unattached.length} UNATTACHED`} bg="var(--gl-gold)" fg="#1c2b2b" />
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={ImageIcon}
          title="No media yet"
          body="Tap Upload Photos to add floor-walk images, or Take Photo to capture from your camera. You can tag each one to a finding or strength after they upload."
        />
      ) : (
        <>
          {unattached.length > 0 && (
            <Section
              title="UNATTACHED PHOTOS"
              subtitle="Tag each photo to a finding or strength so it appears in the report."
            >
              <Grid
                items={unattached}
                findings={findings}
                strengths={strengths}
                onChange={load}
                onDelete={deleteMedia}
              />
            </Section>
          )}
          {attached.length > 0 && (
            <Section title="ATTACHED PHOTOS" subtitle="These will appear in the report PDF.">
              <Grid
                items={attached}
                findings={findings}
                strengths={strengths}
                onChange={load}
                onDelete={deleteMedia}
              />
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function UploadZone({ online, uploadingCount, onPick, onCamera }) {
  return (
    <div
      className="border-2 border-dashed rounded-sm p-6 text-center"
      style={{ borderColor: 'var(--gl-gold)', background: '#fdfaf2' }}
      data-testid="media-upload-zone"
    >
      <p className="text-sm uppercase tracking-widest font-bold" style={{ color: 'var(--gl-dark)', fontFamily: 'Inter' }}>
        Floor Walk Media
      </p>
      <p className="text-xs mt-1" style={{ color: 'var(--gl-muted)' }}>
        Drop unlimited photos here. iOS Safari caps the file picker at 5 per tap - just tap Upload again to add more.
      </p>
      <div className="flex flex-wrap gap-3 justify-center mt-4">
        <button
          type="button"
          onClick={onCamera}
          disabled={!online || uploadingCount > 0}
          className="px-5 py-3 rounded-sm font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
          style={{ background: 'var(--gl-dark-navy)', color: 'var(--gl-gold)' }}
          data-testid="media-camera-button"
        >
          <CameraIcon size={18} weight="fill" /> Take Photo
        </button>
        <button
          type="button"
          onClick={onPick}
          disabled={!online || uploadingCount > 0}
          className="px-5 py-3 rounded-sm font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
          style={{ background: 'var(--gl-gold)', color: 'var(--gl-dark)' }}
          data-testid="media-upload-button"
        >
          <UploadSimpleIcon size={18} weight="bold" /> Upload Photos
        </button>
      </div>
      {uploadingCount > 0 && (
        <p className="text-xs mt-3 font-bold" style={{ color: 'var(--gl-navy)' }}>
          Uploading {uploadingCount} photo{uploadingCount > 1 ? 's' : ''}...
        </p>
      )}
      {!online && (
        <p className="text-xs mt-3" style={{ color: 'var(--gl-p1)' }}>
          Offline - reconnect to upload.
        </p>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between border-b pb-1 mb-3" style={{ borderColor: 'var(--gl-gold)' }}>
        <h3 className="text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--gl-dark)', fontFamily: 'Inter' }}>
          {title}
        </h3>
        {subtitle && <span className="text-xs" style={{ color: 'var(--gl-muted)' }}>{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function Grid({ items, findings, strengths, onChange, onDelete }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((m) => (
        <MediaCard
          key={m.id}
          m={m}
          findings={findings}
          strengths={strengths}
          onChange={onChange}
          onDelete={() => onDelete(m)}
        />
      ))}
    </div>
  );
}

function MediaCard({ m, findings, strengths, onChange, onDelete }) {
  const [caption, setCaption] = useState(m.caption || '');
  const [savingCaption, setSavingCaption] = useState(false);
  const [target, setTarget] = useState('');

  async function saveCaption() {
    if (caption === (m.caption || '')) return;
    setSavingCaption(true);
    try {
      await api.patch(`/media/${m.id}`, { caption });
      toast.success('Caption saved');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCaption(false);
    }
  }

  async function attach() {
    if (!target) return;
    const [kind, id] = target.split('::');
    try {
      await api.post(`/media/${m.id}/attach`, { target_kind: kind, target_id: id });
      toast.success(`Attached to ${kind}`);
      setTarget('');
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function detach(att) {
    try {
      await api.post(`/media/${m.id}/detach`, { target_kind: att.kind, target_id: att.id });
      toast.success('Detached');
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const attachments = m.attachments || [];

  return (
    <div className="border-2 rounded-sm bg-white overflow-hidden flex flex-col" style={{ borderColor: attachments.length ? 'var(--gl-p3)' : 'var(--gl-gold)' }}>
      <a href={mediaUrl(m.id)} target="_blank" rel="noreferrer" className="block bg-gray-100">
        <img src={mediaUrl(m.id)} alt={m.original_filename || ''} className="w-full h-48 object-cover" />
      </a>
      <div className="p-3 flex flex-col gap-2 flex-1">
        <input
          type="text"
          placeholder="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onBlur={saveCaption}
          className="text-sm border-b border-gray-300 focus:border-amber-500 outline-none py-1"
          style={{ fontFamily: 'Inter' }}
          data-testid="media-caption-input"
        />
        {savingCaption && <span className="text-xs text-gray-400">Saving...</span>}

        {/* Existing attachments */}
        {attachments.map((att) => (
          <div key={`${att.kind}-${att.id}`} className="flex items-start justify-between gap-2 text-xs bg-gray-50 rounded-sm px-2 py-1">
            <span className="flex-1 flex items-start gap-1" style={{ color: 'var(--gl-dark)' }}>
              <CheckCircleIcon size={14} weight="fill" style={{ color: 'var(--gl-p3)', flexShrink: 0, marginTop: 2 }} />
              <span>{att.label}</span>
            </span>
            <button
              type="button"
              onClick={() => detach(att)}
              className="text-xs uppercase tracking-widest font-bold hover:underline"
              style={{ color: 'var(--gl-p1)' }}
              data-testid={`media-detach-${att.kind}`}
            >
              Detach
            </button>
          </div>
        ))}

        {/* Attach to dropdown */}
        <div className="flex gap-2 mt-1">
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="flex-1 text-xs border border-gray-300 rounded-sm px-2 py-2 bg-white"
            style={{ fontFamily: 'Inter' }}
            data-testid="media-attach-select"
          >
            <option value="">Attach to...</option>
            {findings.length > 0 && (
              <optgroup label="Findings">
                {findings.map((f) => (
                  <option key={f.id} value={`finding::${f.id}`}>
                    #{f.number} [{f.priority}] {(f.title || '').slice(0, 55)}
                  </option>
                ))}
              </optgroup>
            )}
            {strengths.length > 0 && (
              <optgroup label="Strengths">
                {strengths.map((s) => (
                  <option key={s.id} value={`strength::${s.id}`}>
                    {(s.title || '').slice(0, 60)}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <button
            type="button"
            onClick={attach}
            disabled={!target}
            className="px-3 py-2 rounded-sm font-bold text-xs uppercase tracking-widest disabled:opacity-40"
            style={{ background: 'var(--gl-navy)', color: '#fff' }}
            data-testid="media-attach-button"
          >
            <PaperclipIcon size={14} weight="bold" />
          </button>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
          <span>{m.original_filename}</span>
          <button
            type="button"
            onClick={onDelete}
            className="hover:text-red-600 inline-flex items-center gap-1"
            style={{ color: 'var(--gl-muted)' }}
            data-testid="media-delete-button"
            title="Delete photo"
          >
            <TrashIcon size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Pill({ label, bg, fg }) {
  return (
    <span className="px-3 py-1 rounded-sm" style={{ background: bg, color: fg }}>
      {label}
    </span>
  );
}
